/**
 * Post-login redirect guard.
 *
 * The route guard stores the page a signed-out visitor asked for
 * (`location.pathname + location.search`) and the login page navigates
 * there after sign-in. The pathname comes straight from the URL, so a
 * crafted link such as `https://www.sleepercising.com/\evil.example` would
 * turn the login into an open redirect. react-router 6 only partially
 * guards against this (GHSA-wrjc-x8rr-h8h6 is fixed in 7.18 only), so the
 * target is checked here before navigating.
 */

/** Where the login page lands when there is no usable target. */
export const DEFAULT_LOGIN_TARGET = '/dashboard';

/**
 * Returns `target` when it is an in-app path on the current origin,
 * otherwise `fallback`. Rejects protocol-relative (`//host`) and
 * backslash (`/\host`) forms, which browsers resolve to another origin.
 */
export function safeRedirectTarget(target: unknown, fallback: string = DEFAULT_LOGIN_TARGET): string {
  if (typeof target !== 'string' || !target.startsWith('/') || target.includes('\\')) {
    return fallback;
  }
  try {
    const origin = window.location.origin;
    return new URL(target, origin).origin === origin ? target : fallback;
  } catch {
    return fallback;
  }
}
